// =============================================
// Call Center Layout - מוקד חרום
// Kipikid - מערכת נוכחות ילדים בגנים
// =============================================

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  LogOut,
  Menu,
  X,
  Headphones,
  Building2,
  HelpCircle,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useSimulationStore } from '../../stores/simulationStore';
import { useHelpGuide } from '../../hooks/useHelpGuide';
import HelpGuideModal from '../../components/common/HelpGuideModal';
import { callCenterColors } from '../../styles/colors';

const colors = {
  ...callCenterColors,
  primaryDark: '#0f766e',
  background: '#f9fafb',
};

const navItems = [
  { path: '/callcenter/dashboard', icon: Home, label: 'לוח בקרה' },
];

export default function CallCenterLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<number | undefined>(undefined);
  const [organizations, setOrganizations] = useState<{ id: number; name: string }[]>([]);
  const navigate = useNavigate();
  const { logout, user, isAuthenticated } = useAuthStore();
  const simActive = useSimulationStore(s => s.active);
  const helpGuide = useHelpGuide();

  useEffect(() => {
    document.title = 'קיפיקיד — מוקד חירום';
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', '#eab308');
  }, []);

  // Route guard — callcenter requires role 'callcenter' (serverRole maps to roleTypeId 7)
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/callcenter/login', { replace: true });
      return;
    }
    if (user && user.role !== 'admin' && user.role !== 'sysadmin' && user.serverRole !== 'CallCenter') {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const orgName = user?.organizationName;

  return (
    <div dir="rtl" style={{
      minHeight: '100vh',
      backgroundColor: colors.background,
      display: 'flex',
      paddingTop: simActive ? '36px' : 0,
    }}>
      {/* Mobile Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        height: '60px',
        backgroundColor: 'white',
        borderBottom: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 16px',
        zIndex: 40,
      }}
      className="cc-md-hidden"
      >
        <button
          onClick={() => setSidebarOpen(true)}
          style={{
            padding: '8px',
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
        >
          <Menu size={24} color={colors.text} />
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Headphones size={20} color={colors.primary} />
          <h1 style={{ fontSize: '18px', fontWeight: 'bold', color: colors.primary }}>
            מוקד חרום
          </h1>
        </div>
        <button
          onClick={helpGuide.open}
          style={{ padding: '8px', backgroundColor: 'transparent', border: 'none', cursor: 'pointer' }}
          title="מדריך עזרה (Ctrl+/)"
        >
          <HelpCircle size={22} color={colors.textLight} />
        </button>
      </div>

      {/* Sidebar Overlay (Mobile) */}
      {sidebarOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={() => setSidebarOpen(false)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 45,
          }}
          className="cc-md-hidden"
        />
      )}

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ x: sidebarOpen ? 0 : '-100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '260px',
          backgroundColor: 'white',
          borderLeft: `1px solid ${colors.border}`,
          zIndex: 50,
          display: 'flex',
          flexDirection: 'column',
          transform: 'translateX(-100%)',
        }}
        className="callcenter-sidebar-desktop"
      >
        {/* Sidebar Header */}
        <div style={{
          padding: '20px',
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <img
              src="/kipikid-logo.png"
              alt="KipiKid"
              style={{ width: '52px', height: '52px', objectFit: 'contain' }}
            />
            <div>
              <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: colors.primary }}>
                KipiKid
              </h2>
              <p style={{ fontSize: '12px', color: colors.textLight }}>מוקד חרום</p>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            style={{
              padding: '8px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            }}
            className="cc-md-hidden"
          >
            <X size={20} color={colors.textLight} />
          </button>
        </div>

        {/* Organization Filter */}
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${colors.border}`,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Building2 size={16} color={colors.primary} />
            <span style={{ fontSize: '12px', color: colors.textLight, fontWeight: '500' }}>סינון ארגון</span>
          </div>
          <select
            value={selectedOrganizationId ?? ''}
            onChange={(e) => setSelectedOrganizationId(e.target.value ? Number(e.target.value) : undefined)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: '8px',
              border: `1px solid ${selectedOrganizationId ? colors.primary : colors.border}`,
              backgroundColor: selectedOrganizationId ? '#f0fdfa' : 'white',
              fontSize: '13px',
              color: colors.text,
              direction: 'rtl',
              cursor: 'pointer',
              outline: 'none',
            }}
          >
            <option value="">כל הארגונים</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </select>
        </div>

        {/* Navigation */}
        <nav style={{ flex: 1, padding: '16px 12px', overflowY: 'auto' }}>
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                borderRadius: '10px',
                marginBottom: '4px',
                textDecoration: 'none',
                backgroundColor: isActive ? colors.primaryLight : 'transparent',
                color: isActive ? colors.primary : colors.text,
                fontWeight: isActive ? '600' : '500',
                fontSize: '14px',
                transition: 'all 0.2s',
              })}
            >
              <item.icon size={20} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div style={{
          padding: '16px',
          borderTop: `1px solid ${colors.border}`,
        }}>
          {user && (
            <div style={{ marginBottom: '12px', padding: '12px', backgroundColor: colors.background, borderRadius: '10px' }}>
              <p style={{ fontSize: '14px', fontWeight: '500', color: colors.text }}>{user.name}</p>
              <p style={{ fontSize: '12px', color: colors.primary }}>מוקדן</p>
            </div>
          )}
          <button
            onClick={helpGuide.open}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '10px',
              backgroundColor: '#e0f2fe',
              color: '#0369a1',
              border: 'none',
              borderRadius: '10px',
              fontSize: '13px',
              fontWeight: '500',
              cursor: 'pointer',
              marginBottom: '8px',
            }}
          >
            <HelpCircle size={16} />
            מדריך עזרה (Ctrl+/)
          </button>
          <button
            onClick={handleLogout}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '12px',
              backgroundColor: '#fee2e2',
              color: '#dc2626',
              border: 'none',
              borderRadius: '10px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            <LogOut size={18} />
            התנתקות
          </button>
        </div>
      </motion.aside>

      {/* Main Content */}
      <main style={{
        flex: 1,
        marginRight: '0',
        paddingTop: '76px',
        padding: '76px 16px 16px',
      }}
      className="callcenter-main-content-desktop"
      >
        <Outlet context={{ selectedOrganizationId, setOrganizations }} />
      </main>

      <HelpGuideModal isOpen={helpGuide.isOpen} onClose={helpGuide.close} currentPath={helpGuide.currentPath} userRole={helpGuide.userRole} />

      {/* Desktop Styles */}
      <style>{`
        @media (min-width: 768px) {
          .cc-md-hidden {
            display: none !important;
          }
          .callcenter-sidebar-desktop {
            transform: translateX(0) !important;
          }
          .callcenter-main-content-desktop {
            margin-right: 260px !important;
            padding-top: 16px !important;
          }
        }
      `}</style>
    </div>
  );
}
